(function (global) {
  'use strict';
  // Adapted from the deployed Castlewood account-project adapter.
  // Credentials stay in this parent page; the opaque vanity iframe receives no tokens.
  const READ = `query CastlewoodProjects($namespace: String!, $key: String!) {
    customer { id metafield(namespace: $namespace, key: $key) { type jsonValue compareDigest } }
  }`;
  const WRITE = `mutation CastlewoodProjectsSave($metafields: [MetafieldsSetInput!]!) {
    metafieldsSet(metafields: $metafields) {
      metafields { key namespace compareDigest }
      userErrors { code field message }
    }
  }`;
  function failure(code, reason) { const error = new Error(code); error.code = code; if (reason) error.reason = reason; return error; }
  const clone = value => JSON.parse(JSON.stringify(value));
  const string = (value, max) => typeof value === 'string' && value.length <= max;
  const validId = value => string(value, 100) && /^[A-Za-z0-9_-]{8,100}$/.test(value);
  const validDate = value => string(value, 40) && Number.isFinite(Date.parse(value));
  const utf8 = value => new TextEncoder().encode(value);
  function base64url(bytes) {
    let raw = ''; bytes.forEach(value => { raw += String.fromCharCode(value); });
    return global.btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }
  function decode64(value) {
    if (typeof value !== 'string' || !/^[A-Za-z0-9_-]+$/.test(value)) throw failure('AUTH_INVALID');
    const raw = global.atob(value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '='));
    return Uint8Array.from(raw, c => c.charCodeAt(0));
  }
  function jsonSegment(value) { return JSON.parse(new TextDecoder().decode(decode64(value))); }
  function documentValue(value) {
    if (!value || value.version !== 1 || !Array.isArray(value.projects) || value.projects.length > 50) throw failure('PROJECT_DATA_INVALID');
    const ids = new Set();
    for (const project of value.projects) {
      if (!project || !validId(project.id) || ids.has(project.id) || !string(project.name, 100) || !project.name.trim() || !validDate(project.updatedAt) || !project.snapshot || typeof project.snapshot !== 'object' || Array.isArray(project.snapshot)) throw failure('PROJECT_DATA_INVALID');
      ids.add(project.id);
    }
    return clone(value);
  }
  function createVanityAccountAdapter(config = {}, callbackUrl = null) {
    const origin = 'https://www.castlewoodcabinetry.com';
    const shopId = '70039896369';
    const publicUri = origin + '/pages/frameless-room-builder';
    const reviewUri = publicUri + '?preview_theme_id=189760962865';
    const redirectUri = config.redirectUri || publicUri;
    const storefrontId = config.storefrontCustomerId == null ? null : String(config.storefrontCustomerId);
    const storefrontCustomerKey = storefrontId && /^\d+$/.test(storefrontId) ? 'gid://shopify/Customer/' + storefrontId : null;
    const expectedIssuer = 'https://shopify.com/authentication/' + shopId;
    const ready = config.enabled === true && string(config.clientId, 200) && /^[A-Za-z0-9_-]{8,200}$/.test(config.clientId)
      && (!config.shopId || String(config.shopId) === shopId)
      && (!config.shopDomain || config.shopDomain === origin)
      && (redirectUri === publicUri || redirectUri === reviewUri)
      && (storefrontId === null || !!storefrontCustomerKey)
      && global.location.origin === origin;
    const clientId = config.clientId || '';
    const namespace = 'custom', key = 'castlewood_vanity_designs';
    const prefix = 'cw-vanity-account:' + shopId + ':' + clientId + ':';
    const tokenKey = prefix + 'token', transactionKey = prefix + 'oauth', returnKey = prefix + 'return';
    let discoveryPromise, callbackPromise, refreshPromise, token = null, owner = null, generation = 0;
    const now = () => Date.now();
    const random = () => base64url(global.crypto.getRandomValues(new Uint8Array(32)));
    function storage() { try { return global.sessionStorage; } catch (_) { throw failure('STORAGE_UNAVAILABLE'); } }
    function read(key) { try { const raw = storage().getItem(key); return raw ? JSON.parse(raw) : null; } catch (_) { return null; } }
    function write(key, value) {
      try { const raw = JSON.stringify(value); storage().setItem(key, raw); if (storage().getItem(key) !== raw) throw new Error(); }
      catch (_) { throw failure('STORAGE_UNAVAILABLE'); }
    }
    function remove(key) { try { storage().removeItem(key); } catch (_) {} }
    function clear() {
      token = null; owner = null; generation += 1; remove(tokenKey); remove(transactionKey);
      try { const store = storage(); for (let i = store.length - 1; i >= 0; i--) { const k = store.key(i); if (k && k.startsWith(prefix + 'draft:')) store.removeItem(k); } } catch (_) {}
    }
    function cleanReturn(value) { return safeVanityReturn(value, config.review === true); }
    function endpoint(value) {
      let url; try { url = new URL(value); } catch (_) { throw failure('AUTH_INVALID'); }
      const local = url.hostname === 'www.castlewoodcabinetry.com' || url.hostname === 'account.castlewoodcabinetry.com' || url.hostname === 'accounts.castlewoodcabinetry.com';
      const shopify = url.hostname === 'shopify.com' && (url.pathname.startsWith('/authentication/' + shopId + '/') || url.pathname.startsWith('/' + shopId + '/account/'));
      if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash || (!local && !shopify)) throw failure('AUTH_INVALID');
      return url.href;
    }
    async function request(url, options = {}) {
      const controller = new AbortController();
      const timer = global.setTimeout(() => controller.abort(), 15000);
      try { return await global.fetch(url, { ...options, signal: controller.signal, credentials: 'omit', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer' }); }
      catch (_) { throw failure('NETWORK_ERROR'); }
      finally { global.clearTimeout(timer); }
    }
    async function readJson(response) {
      let timer;
      try {
        return await Promise.race([response.json(), new Promise((_, reject) => { timer = global.setTimeout(() => reject(failure('NETWORK_ERROR')), 15000); })]);
      } catch (_) { throw failure('NETWORK_ERROR'); }
      finally { global.clearTimeout(timer); }
    }
    async function discovery() {
      if (!ready) throw failure('NOT_CONFIGURED');
      if (!discoveryPromise) discoveryPromise = (async () => {
        const responses = await Promise.all([request(origin + '/.well-known/openid-configuration'), request(origin + '/.well-known/customer-account-api')]);
        if (responses.some(response => !response.ok)) throw failure('NETWORK_ERROR');
        const [auth, api] = await Promise.all(responses.map(response => readJson(response)));
        if (auth.issuer !== expectedIssuer) throw failure('AUTH_INVALID','AUTH_DISCOVERY_ISSUER');
        if (!auth.code_challenge_methods_supported?.includes('S256')) throw failure('AUTH_INVALID','AUTH_DISCOVERY_PKCE');
        return { issuer: auth.issuer, authorize: endpoint(auth.authorization_endpoint), token: endpoint(auth.token_endpoint), logout: endpoint(auth.end_session_endpoint), jwks: endpoint(auth.jwks_uri), graphql: endpoint(api.graphql_api) };
      })().catch(error => { discoveryPromise = null; throw error; });
      return discoveryPromise;
    }
    async function validateTokenEndpointIdToken(raw, nonce, subject) {
      try {
        const parts = raw.split('.');
        if (parts.length !== 3 || !parts[2] || !/^[A-Za-z0-9_-]+$/.test(parts[2])) throw failure('AUTH_INVALID','AUTH_TOKEN_FORMAT');
        const header = jsonSegment(parts[0]), claims = jsonSegment(parts[1]), auth = await discovery();
        if (header.alg !== 'RS256') throw failure('AUTH_INVALID','AUTH_TOKEN_ALGORITHM');
        if (!string(header.kid,200)) throw failure('AUTH_INVALID','AUTH_TOKEN_KEY_ID');
        if (claims.iss !== auth.issuer) throw failure('AUTH_INVALID','AUTH_TOKEN_ISSUER');
        if (!(claims.aud === clientId || (Array.isArray(claims.aud) && claims.aud.includes(clientId)))) throw failure('AUTH_INVALID','AUTH_TOKEN_AUDIENCE');
        if ((Array.isArray(claims.aud) && claims.aud.length > 1 && claims.azp !== clientId) || (claims.azp && claims.azp !== clientId)) throw failure('AUTH_INVALID','AUTH_TOKEN_AUTHORIZED_PARTY');
        if (!Number.isFinite(claims.exp)) throw failure('AUTH_INVALID','AUTH_TOKEN_EXPIRY_FORMAT');
        if (claims.exp * 1000 <= now()) throw failure('AUTH_INVALID','AUTH_TOKEN_EXPIRED');
        if (!Number.isFinite(claims.iat)) throw failure('AUTH_INVALID','AUTH_TOKEN_ISSUED_FORMAT');
        if (claims.iat * 1000 > now()+60000) throw failure('AUTH_INVALID','AUTH_TOKEN_ISSUED_FUTURE');
        if (claims.sub === undefined || claims.sub === null) throw failure('AUTH_INVALID','AUTH_TOKEN_SUBJECT_MISSING');
        if (typeof claims.sub === 'number') {
          if (!Number.isSafeInteger(claims.sub) || claims.sub<=0) throw failure('AUTH_INVALID','AUTH_TOKEN_SUBJECT_NUMBER_INVALID');
          claims.sub = String(claims.sub);
        }
        if (typeof claims.sub !== 'string') throw failure('AUTH_INVALID','AUTH_TOKEN_SUBJECT_TYPE');
        if (!claims.sub) throw failure('AUTH_INVALID','AUTH_TOKEN_SUBJECT_EMPTY');
        if (claims.sub.length>300) throw failure('AUTH_INVALID','AUTH_TOKEN_SUBJECT_LONG');
        if (nonce && claims.nonce !== nonce) throw failure('AUTH_INVALID','AUTH_TOKEN_NONCE');
        if (subject && claims.sub !== subject) throw failure('AUTH_INVALID','AUTH_TOKEN_REFRESH_SUBJECT');
        return claims;
      } catch (error) { if (error.reason) throw error; throw failure('AUTH_INVALID','AUTH_TOKEN_PARSE'); }
    }
    async function acceptToken(response, nonce, previous, startedGeneration) {
      const auth = await discovery();
      if (response.url !== auth.token) throw failure('AUTH_INVALID','AUTH_TOKEN_RESPONSE_URL');
      if (response.redirected) throw failure('AUTH_INVALID','AUTH_TOKEN_RESPONSE_REDIRECT');
      if (!['basic','cors'].includes(response.type)) throw failure('AUTH_INVALID','AUTH_TOKEN_RESPONSE_TYPE');
      if (!response.ok) { clear(); throw failure('AUTH_REQUIRED'); }
      const body = await readJson(response);
      if (!string(body.access_token,16384) || !body.access_token) throw failure('AUTH_INVALID','AUTH_ACCESS_TOKEN_FORMAT');
      if (!Number.isFinite(body.expires_in) || body.expires_in<=0 || body.expires_in>604800) throw failure('AUTH_INVALID','AUTH_ACCESS_TOKEN_LIFETIME');
      let idToken, subject, accountLabel;
      if (previous && body.id_token === undefined) {
        if (!string(previous.idToken,32768) || !previous.idToken || !string(previous.subject,300) || !previous.subject) throw failure('AUTH_INVALID','AUTH_REFRESH_IDENTITY_MISSING');
        idToken = previous.idToken; subject = previous.subject;
        accountLabel = previous.accountLabel || 'Customer account';
      } else {
        if (!string(body.id_token,32768) || !body.id_token) throw failure('AUTH_INVALID','AUTH_ID_TOKEN_MISSING');
        const claims = await validateTokenEndpointIdToken(body.id_token, nonce, previous?.subject);
        idToken = body.id_token; subject = claims.sub;
        accountLabel = claims.email_verified === true && string(claims.email,320) ? claims.email : 'Customer account';
      }
      if (startedGeneration !== generation) throw failure('AUTH_CHANGED','AUTH_SESSION_INVALIDATED');
      token = {accessToken:body.access_token,idToken,refreshToken:string(body.refresh_token,16384) ? body.refresh_token : previous?.refreshToken,expiresAt:now()+body.expires_in*1000,sessionUntil:previous?.sessionUntil || now()+8*60*60*1000,subject,apiCustomerKey:previous?.apiCustomerKey || null,storefrontCustomerKey:previous?.storefrontCustomerKey || storefrontCustomerKey,accountLabel};
      write(tokenKey, token); return token;
    }
    async function login(returnTo, active = () => {}) {
      const auth = await discovery();
      active();
      clear();
      const transaction = {state:random(),nonce:random(),verifier:random(),createdAt:now(),returnTo:cleanReturn(returnTo)};
      write(transactionKey, transaction);
      const challenge = base64url(new Uint8Array(await global.crypto.subtle.digest('SHA-256', utf8(transaction.verifier))));
      active();
      const url = new URL(auth.authorize);
      Object.entries({client_id:clientId,scope:'openid email customer-account-api:full',response_type:'code',redirect_uri:redirectUri,state:transaction.state,nonce:transaction.nonce,code_challenge:challenge,code_challenge_method:'S256'}).forEach(([k,v]) => url.searchParams.set(k,v));
      global.location.assign(url.href);
    }
    async function callback() {
      if (!callbackUrl) return;
      const url = new URL(callbackUrl);
      const hasCallback = url.searchParams.has('code') || url.searchParams.has('error');
      if (!hasCallback) {
        const savedReturn = read(returnKey); remove(returnKey);
        if (savedReturn) global.history.replaceState(null, '', cleanReturn(savedReturn));
        return;
      }
      const transaction = read(transactionKey); remove(transactionKey);
      // The synchronous relay already removed OAuth parameters before room scripts run.
      const invalidCallback = reason => { clear(); throw failure('AUTH_INVALID',reason); };
      if (!transaction) invalidCallback('AUTH_CALLBACK_TRANSACTION_MISSING');
      if (!string(transaction.state,100) || transaction.state !== url.searchParams.get('state')) invalidCallback('AUTH_CALLBACK_STATE');
      if (!string(transaction.nonce,100) || !string(transaction.verifier,128)) invalidCallback('AUTH_CALLBACK_VERIFIER');
      if (!Number.isFinite(transaction.createdAt) || now()-transaction.createdAt>10*60*1000 || transaction.createdAt>now()+60000) invalidCallback('AUTH_CALLBACK_EXPIRED');
      if (url.searchParams.getAll('state').length!==1 || url.searchParams.getAll('code').length>1) invalidCallback('AUTH_CALLBACK_DUPLICATE');
      if (url.searchParams.has('error')) { clear(); throw failure('AUTH_REQUIRED'); }
      const code = url.searchParams.get('code'); if (!string(code,4096) || !code) { clear(); throw failure('AUTH_INVALID','AUTH_CALLBACK_CODE'); }
      const auth = await discovery(), startedGeneration = generation;
      const body = new URLSearchParams({grant_type:'authorization_code',client_id:clientId,redirect_uri:redirectUri,code,code_verifier:transaction.verifier});
      try { await acceptToken(await request(auth.token,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body}),transaction.nonce,null,startedGeneration); }
      catch (error) { clear(); throw error; }
    }
    function initialize() {
      if (!ready) return Promise.resolve();
      if (!callbackPromise) callbackPromise = callback();
      return callbackPromise;
    }
    async function ensureToken() {
      if (!ready) throw failure('NOT_CONFIGURED');
      await initialize();
      const persisted = read(tokenKey);
      if (token && (!persisted || persisted.accessToken !== token.accessToken || persisted.subject !== token.subject)) { clear(); throw failure('AUTH_CHANGED','AUTH_STORED_SESSION_CHANGED'); }
      if (!token) token = persisted;
      if (!token || !string(token.accessToken,16384) || !token.accessToken || !Number.isFinite(token.expiresAt) || !Number.isFinite(token.sessionUntil) || token.sessionUntil <= now() || !string(token.subject,300)) { clear(); throw failure('AUTH_REQUIRED'); }
      if (token.storefrontCustomerKey && token.storefrontCustomerKey !== storefrontCustomerKey) { clear(); throw failure('AUTH_CHANGED','AUTH_STOREFRONT_SESSION_CHANGED'); }
      if (token.expiresAt > now()+60000) return token;
      if (!token.refreshToken) { clear(); throw failure('AUTH_REQUIRED'); }
      if (!refreshPromise) refreshPromise = (async () => {
        const auth = await discovery(), previous = token, startedGeneration = generation;
        const body = new URLSearchParams({grant_type:'refresh_token',client_id:clientId,refresh_token:previous.refreshToken});
        try { return await acceptToken(await request(auth.token,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body}),null,previous,startedGeneration); }
        catch (error) { clear(); throw error; }
      })().finally(() => { refreshPromise = null; });
      return refreshPromise;
    }
    async function graphql(query, variables) {
      const startedGeneration = generation;
      const current = await ensureToken(), auth = await discovery();
      if (startedGeneration !== generation) throw failure('AUTH_CHANGED','AUTH_SESSION_INVALIDATED');
      const response = await request(auth.graphql,{method:'POST',headers:{'Content-Type':'application/json',Authorization:current.accessToken},body:JSON.stringify({query,variables})});
      if (startedGeneration !== generation) throw failure('AUTH_CHANGED','AUTH_SESSION_INVALIDATED');
      if (response.status === 401 || response.status === 403) { clear(); throw failure('AUTH_REQUIRED'); }
      if (!response.ok) throw failure('NETWORK_ERROR');
      const result = await readJson(response);
      if (startedGeneration !== generation) throw failure('AUTH_CHANGED','AUTH_SESSION_INVALIDATED');
      if (result.errors?.length) {
        if (result.errors.some(error => ['UNAUTHORIZED','ACCESS_DENIED','UNAUTHENTICATED'].includes(error.extensions?.code))) { clear(); throw failure('AUTH_REQUIRED'); }
        throw failure('SAVE_REJECTED');
      }
      if (!result.data) throw failure('SAVE_REJECTED');
      return result.data;
    }
    async function readProjects(customerKey) {
      const startedGeneration = generation;
      const data = await graphql(READ,{namespace,key});
      if (startedGeneration !== generation) throw failure('AUTH_CHANGED','AUTH_SESSION_INVALIDATED');
      const customer = data.customer;
      if (!customer || !/^gid:\/\/shopify\/Customer\/\d+$/.test(customer.id)) { clear(); throw failure('AUTH_REQUIRED'); }
      if (customerKey && customerKey !== customer.id) { clear(); throw failure('AUTH_CHANGED','AUTH_EXPECTED_CUSTOMER_CHANGED'); }
      if (owner && owner !== customer.id) { clear(); throw failure('AUTH_CHANGED','AUTH_API_CUSTOMER_CHANGED'); }
      if (storefrontCustomerKey && storefrontCustomerKey !== customer.id) { clear(); throw failure('AUTH_CHANGED','AUTH_STOREFRONT_CUSTOMER_MISMATCH'); }
      if (token?.apiCustomerKey && token.apiCustomerKey !== customer.id) { clear(); throw failure('AUTH_CHANGED','AUTH_TOKEN_API_CUSTOMER_CHANGED'); }
      owner = customer.id;
      if (token && (token.apiCustomerKey !== customer.id || (storefrontCustomerKey && token.storefrontCustomerKey !== storefrontCustomerKey))) {
        token.apiCustomerKey = customer.id;
        if (storefrontCustomerKey) token.storefrontCustomerKey = storefrontCustomerKey;
        write(tokenKey,token);
      }
      const field = customer.metafield;
      if (field && (field.type !== 'json' || !string(field.compareDigest,128) || !field.compareDigest)) throw failure('PROJECT_DATA_INVALID');
      return {customerKey:owner,document:field ? documentValue(field.jsonValue) : {version:1,projects:[]},digest:field?.compareDigest ?? null};
    }
    async function session() {
      if (!ready) return {authenticated:false,customerKey:null};
      try { const current = await readProjects(); return {authenticated:true,customerKey:current.customerKey,accountLabel:token?.accountLabel || 'Customer account'}; }
      catch (error) { if (error.code === 'AUTH_REQUIRED') return {authenticated:false,customerKey:null}; throw error; }
    }
    async function list(options = {}) { const current = await readProjects(options.customerKey); return clone(current.document.projects).sort((a,b) => b.updatedAt.localeCompare(a.updatedAt)); }
    async function get(id, options = {}) { const current = await readProjects(options.customerKey); return clone(current.document.projects.find(project => project.id === id) || null); }
    async function save(project, options = {}) {
      if (!options.customerKey) throw failure('AUTH_REQUIRED');
      if (!project || !validId(project.id) || !string(project.name,100) || !project.name.trim() || !project.snapshot || typeof project.snapshot !== 'object' || Array.isArray(project.snapshot)) throw failure('PROJECT_INVALID');
      const input = {id:project.id,name:project.name.trim(),snapshot:clone(project.snapshot)};
      const expected = options.expectedUpdatedAt ?? null;
      if (expected !== null && !validDate(expected)) throw failure('PROJECT_INVALID');
      for (let attempt = 0; attempt < 3; attempt++) {
        const current = await readProjects(options.customerKey);
        const existing = current.document.projects.find(item => item.id === input.id);
        if ((existing?.updatedAt ?? null) !== expected) throw failure('PROJECT_CONFLICT');
        if (!existing && current.document.projects.length >= 50) throw failure('PROJECT_LIMIT');
        const updatedAt = new Date(Math.max(now(),existing ? Date.parse(existing.updatedAt)+1 : 0)).toISOString();
        const saved = {...input,updatedAt};
        const projects = current.document.projects.filter(item => item.id !== input.id).concat(saved);
        const value = JSON.stringify({...current.document,version:1,projects});
        if (utf8(value).length > 120000) throw failure('PROJECT_TOO_LARGE');
        const result = await graphql(WRITE,{metafields:[{ownerId:current.customerKey,namespace,key,type:'json',value,compareDigest:current.digest}]});
        const mutation = result.metafieldsSet, errors = mutation?.userErrors;
        if (!mutation || !Array.isArray(errors)) throw failure('SAVE_REJECTED');
        if (errors.length) {
          if (errors.every(error => error.code === 'STALE_OBJECT')) continue;
          throw failure('SAVE_REJECTED');
        }
        if (!mutation.metafields?.some(field => field.key === key && field.namespace === namespace && field.compareDigest)) throw failure('SAVE_REJECTED');
        return clone(saved);
      }
      throw failure('PROJECT_CONFLICT');
    }
    async function logout(active = () => {}) {
      const previous = token || read(tokenKey), returnTo = cleanReturn();
      clear();
      if (!previous?.idToken || !ready) return;
      write(returnKey,returnTo);
      const auth = await discovery(), url = new URL(auth.logout);
      active();
      url.searchParams.set('id_token_hint',previous.idToken); url.searchParams.set('post_logout_redirect_uri',redirectUri);
      global.location.assign(url.href);
    }
    return Object.freeze({ready,initialize,session,login,logout,invalidate:clear,list,get,save,storagePrefix:prefix});
  }
  const ORIGIN = 'https://www.castlewoodcabinetry.com';
  const CHANNEL = 'castlewood-vanity-account';
  const REVIEW = '189760962865';
  const MAX_BYTES = 100000;
  const CONTINUITY_TTL = 30 * 60 * 1000;
  const record = value => !!value && typeof value === 'object' && !Array.isArray(value);
  const customerId = value => typeof value === 'string' && /^gid:\/\/shopify\/Customer\/\d+$/.test(value);
  const config = record(global.CastlewoodVanityAccountConfig) ? {...global.CastlewoodVanityAccountConfig} : {};
  config.redirectUri = ORIGIN + '/pages/frameless-room-builder' + (config.review === true ? '?preview_theme_id=' + REVIEW : '');
  const prefix = 'cw-vanity-account:70039896369:' + (config.clientId || '') + ':';
  const continuityKey = prefix + 'continuity';
  function safeVanityReturn(value, review) {
    const safe = new URL(ORIGIN + '/pages/vanity-builder');
    if (review) safe.searchParams.set('preview_theme_id', REVIEW);
    try {
      const candidate = new URL(value || global.location.href, ORIGIN);
      if (candidate.origin === ORIGIN && candidate.pathname === safe.pathname && !candidate.username && !candidate.password) {
        if (candidate.searchParams.get('preview_theme_id') === REVIEW) safe.searchParams.set('preview_theme_id', REVIEW);
        ['_fd','pb'].forEach(key => { const v = candidate.searchParams.get(key); if (v === '0' || v === '1') safe.searchParams.set(key,v); });
      }
    } catch (_) {}
    return safe.href;
  }
  function storageRead(key) {
    try { const raw = global.sessionStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
  }
  function storageRemove(key) { try { global.sessionStorage.removeItem(key); } catch (_) {} }
  function storageWrite(key,value) {
    try { const raw = JSON.stringify(value); global.sessionStorage.setItem(key,raw); if (global.sessionStorage.getItem(key) !== raw) throw new Error(); }
    catch (_) { throw failure('STORAGE_UNAVAILABLE'); }
  }
  function validateSnapshot(value) {
    // Validate shape and bounded values without normalizing or changing the saved configuration.
    if (!record(value) || value.application !== 'Castlewood Vanity Atelier' || value.version !== 4 || !record(value.config)) throw failure('INVALID_DESIGN');
    if (!['preset','design'].includes(value.experience) || !(value.presetId === null || string(value.presetId,100))) throw failure('INVALID_DESIGN');
    const c = value.config;
    const options = {
      style:['haven','shaker','oxford','edgeline','slab'],
      stone:['calacatta','white','carrara','dark','none'], hardware:['brass','nickel','black','chrome'],
      mount:['legs','floating','plinth'], sink:['single','double','left','right'], storage:['doors','drawers'],
      pull:['bar','knob','edge'], faucet:['single','widespread','wall'], sheen:['matte','satin'],
      mode:['standard','custom'], construction:['frameless','framed','inset'], finishedEnds:['both','left','right','neither']
    };
    for (const key of Object.keys(options)) if (!options[key].includes(c[key])) throw failure('INVALID_DESIGN');
    const finishKeys = ['pgw','tdw','tgw','twp','sw','sa','sd','sg','si','sn','sb','sh','sc','seo','ow','oc','os','om','ot','dune','sage','harbor','ridge','ember','ew','er'];
    if (!finishKeys.includes(c.finish) || typeof c.assembly !== 'boolean' || typeof c.backsplash !== 'boolean') throw failure('INVALID_DESIGN');
    for (const [key,min,max] of [['width',24,97],['depth',15,24],['height',30,38]]) if (!Number.isFinite(c[key]) || c[key] < min || c[key] > max) throw failure('INVALID_DESIGN');
    if (![.75,1.25,2].includes(c.thickness) || !Array.isArray(c.bays) || c.bays.length < 1 || c.bays.length > 6) throw failure('INVALID_DESIGN');
    const ids = new Set(); let sinks = 0;
    for (const bay of c.bays) {
      if (!record(bay) || !string(bay.id,100) || !bay.id || ids.has(bay.id) || !['sink','drawers','doors','open'].includes(bay.kind) || !Number.isFinite(bay.width) || bay.width <= 0 || bay.width > 97 || ![2,3,4].includes(bay.drawers) || (bay.finish !== undefined && !finishKeys.includes(bay.finish)) || (bay.catalogId !== undefined && (!string(bay.catalogId,100) || !bay.catalogId))) throw failure('INVALID_DESIGN');
      ids.add(bay.id); if (bay.kind === 'sink') sinks++;
    }
    if (sinks < 1 || sinks > 2) throw failure('INVALID_DESIGN');
    let count = 0;
    const walk = (item,depth) => {
      if (++count > 2000 || depth > 20) throw failure('INVALID_DESIGN');
      if (item && typeof item === 'object') for (const key of Object.keys(item)) { if (['__proto__','prototype','constructor'].includes(key)) throw failure('INVALID_DESIGN'); walk(item[key],depth+1); }
      else if (!['string','number','boolean','undefined'].includes(typeof item) && item !== null) throw failure('INVALID_DESIGN');
    };
    walk(value,0);
    try { if (utf8(JSON.stringify(value)).length > MAX_BYTES) throw failure('INVALID_DESIGN'); return clone(value); }
    catch (_) { throw failure('INVALID_DESIGN'); }
  }
  function continuity() {
    const value = storageRead(continuityKey);
    if (!record(value) || !Number.isFinite(value.createdAt) || value.createdAt > Date.now()+60000 || Date.now()-value.createdAt > CONTINUITY_TTL || !['save','list'].includes(value.intent) || !string(value.name,100) || value.returnTo !== safeVanityReturn(value.returnTo,config.review === true)) { storageRemove(continuityKey); return null; }
    try { validateSnapshot(value.snapshot); return value; } catch (_) { storageRemove(continuityKey); return null; }
  }
  const MESSAGES = {
    CONFLICT:'This saved design changed in another window. Open the latest design or save a new copy.',
    AUTH_REQUIRED:'Please sign in to your Castlewood account to continue.',
    SESSION_CHANGED:'Your account session changed. Please sign in again before opening or saving designs.',
    INVALID_REQUEST:'This account request could not be accepted.', INVALID_DESIGN:'This is not a valid Castlewood vanity design.',
    STORAGE_UNAVAILABLE:'Your browser could not keep the design through sign-in. Allow session storage and try again.',
    LIMIT_REACHED:'Your account design storage is full. Download a copy of this design.',
    UNAVAILABLE:'Account saving is temporarily unavailable. Please try again, or download a design file.'
  };
  function publicError(error) {
    const codes = {PROJECT_CONFLICT:'CONFLICT',AUTH_CHANGED:'SESSION_CHANGED',AUTH_INVALID:'AUTH_REQUIRED',PROJECT_LIMIT:'LIMIT_REACHED',PROJECT_TOO_LARGE:'LIMIT_REACHED',PROJECT_INVALID:'INVALID_REQUEST'};
    const code = codes[error?.code] || (Object.prototype.hasOwnProperty.call(MESSAGES,error?.code) ? error.code : 'UNAVAILABLE');
    return {code,message:MESSAGES[code]};
  }
  // This block must run synchronously in <head>, before the room-builder adapter.
  // Only a state belonging to this vanity transaction can claim a room callback.
  let callbackUrl = null, relayReturn = null;
  const currentUrl = new URL(global.location.href);
  if (config.enabled === true && global.location.origin === ORIGIN && currentUrl.pathname === '/pages/frameless-room-builder') {
    const transaction = storageRead(prefix+'oauth');
    const hasCallback = currentUrl.searchParams.has('code') || currentUrl.searchParams.has('error');
    const validTransaction = record(transaction) && /^[A-Za-z0-9_-]{43}$/.test(transaction.state || '') && /^[A-Za-z0-9_-]{43}$/.test(transaction.nonce || '') && /^[A-Za-z0-9_-]{43}$/.test(transaction.verifier || '') && Number.isFinite(transaction.createdAt) && Date.now()-transaction.createdAt <= 10*60*1000 && transaction.createdAt <= Date.now()+60000 && transaction.returnTo === safeVanityReturn(transaction.returnTo,config.review === true);
    if (hasCallback && validTransaction && currentUrl.searchParams.getAll('state').length === 1 && currentUrl.searchParams.get('state') === transaction.state) {
      callbackUrl = currentUrl.href; relayReturn = transaction.returnTo;
      const stripped = new URL(config.redirectUri);
      global.history.replaceState(null,'',stripped.href);
    } else if (!hasCallback) {
      const savedReturn = storageRead(prefix+'return');
      if (typeof savedReturn === 'string' && savedReturn === safeVanityReturn(savedReturn,config.review === true)) { storageRemove(prefix+'return'); relayReturn = savedReturn; }
    }
  }
  const adapter = createVanityAccountAdapter(config,callbackUrl);
  if (relayReturn) {
    Promise.resolve().then(() => adapter.initialize()).catch(error => {
      const kept = continuity(); if (kept) { kept.error = publicError(error); try { storageWrite(continuityKey,kept); } catch (_) {} }
    }).finally(() => global.location.replace(relayReturn));
    return;
  }
  if (global.location.origin !== ORIGIN || currentUrl.pathname !== '/pages/vanity-builder') return;

  let boundFrame = null, frameGeneration = 0, sessionGeneration = 0, knownOwner, authTransition = false;
  let saving = Promise.resolve();
  const seen = new Set(), pending = new Set();
  function frame() {
    const actual = global.document.querySelector('iframe.castlewood-vanity-frame');
    if (actual !== boundFrame) {
      boundFrame = actual; frameGeneration++;
      if (actual) actual.addEventListener('load',() => { if (actual === boundFrame) frameGeneration++; });
    }
    return actual;
  }
  function trackSession(result) {
    const key = result.authenticated ? result.customerKey : null;
    if (knownOwner !== undefined && key !== knownOwner) sessionGeneration++;
    knownOwner = key;
    return {ready:adapter.ready,authenticated:result.authenticated === true,customerKey:key,accountLabel:result.authenticated ? result.accountLabel || 'Customer account' : ''};
  }
  async function owned(payload) {
    if (!customerId(payload.customerKey)) throw failure('INVALID_REQUEST');
    const result = trackSession(await adapter.session());
    if (!result.authenticated) throw failure('AUTH_REQUIRED');
    if (result.customerKey !== payload.customerKey) { adapter.invalidate(); sessionGeneration++; knownOwner = null; throw failure('AUTH_CHANGED'); }
    return result;
  }
  global.addEventListener('message',event => {
    const actual = frame();
    if (!actual || actual.isConnected === false || event.source !== actual.contentWindow || event.origin !== 'null') return;
    const message = event.data;
    if (!record(message) || message.channel !== CHANNEL || !string(message.id,100) || !/^[A-Za-z0-9_-]{1,100}$/.test(message.id) || seen.has(message.id)) return;
    const source = event.source, frameAtStart = frameGeneration, sessionAtStart = sessionGeneration;
    const reply = value => {
      if (frame() !== actual || frameAtStart !== frameGeneration || actual.isConnected === false || source !== actual.contentWindow) return;
      source.postMessage({channel:CHANNEL,id:message.id,...value},'*');
    };
    const payload = message.payload === undefined ? {} : message.payload;
    try { if (!record(payload) || utf8(JSON.stringify(message)).length > MAX_BYTES || !['session','list','get','save','login','logout','resume'].includes(message.action)) throw failure('INVALID_REQUEST'); }
    catch (error) { reply({ok:false,error:publicError(error)}); return; }
    if (pending.size >= 16) { reply({ok:false,error:publicError(failure('UNAVAILABLE'))}); return; }
    seen.add(message.id); if (seen.size > 500) seen.delete(seen.values().next().value);
    pending.add(message.id);
    const unchanged = () => { if (sessionGeneration !== sessionAtStart || frameGeneration !== frameAtStart) throw failure('AUTH_CHANGED'); };
    const run = async () => {
      unchanged();
      if (authTransition && !['resume'].includes(message.action)) throw failure('UNAVAILABLE');
      switch (message.action) {
        case 'session': { const result = await adapter.session(); unchanged(); return trackSession(result); }
        case 'resume': {
          const kept = continuity(); storageRemove(continuityKey);
          return kept ? {snapshot:clone(kept.snapshot),name:kept.name,intent:kept.intent,...(kept.error ? {error:kept.error} : {})} : null;
        }
        case 'login': {
          if (!adapter.ready) throw failure('NOT_CONFIGURED');
          const snapshot = validateSnapshot(payload.snapshot);
          if (!string(payload.name,100) || !['save','list'].includes(payload.intent)) throw failure('INVALID_REQUEST');
          const returnTo = safeVanityReturn(global.location.href,config.review === true);
          storageWrite(continuityKey,{snapshot,name:payload.name,intent:payload.intent,createdAt:Date.now(),returnTo});
          sessionGeneration++; knownOwner = null; authTransition = true;
          const transition = sessionGeneration;
          const active = () => { if (transition !== sessionGeneration || frameAtStart !== frameGeneration || frame() !== actual) throw failure('AUTH_CHANGED'); };
          try { await adapter.login(returnTo,active); return {redirecting:true}; } finally { authTransition = false; }
        }
        case 'logout': {
          sessionGeneration++; knownOwner = null; authTransition = true; storageRemove(continuityKey);
          const transition = sessionGeneration;
          const active = () => { if (transition !== sessionGeneration || frameAtStart !== frameGeneration || frame() !== actual) throw failure('AUTH_CHANGED'); };
          try { await adapter.logout(active); return {redirecting:true}; } finally { authTransition = false; }
        }
        case 'list': await owned(payload); unchanged(); return adapter.list({customerKey:payload.customerKey});
        case 'get':
          if (!validId(payload.id)) throw failure('INVALID_REQUEST');
          await owned(payload); unchanged(); return adapter.get(payload.id,{customerKey:payload.customerKey});
        case 'save': {
          const snapshot = validateSnapshot(payload.snapshot);
          if ((payload.id !== null && payload.id !== undefined && !validId(payload.id)) || !string(payload.name,100) || !payload.name.trim() || (payload.expectedUpdatedAt != null && !validDate(payload.expectedUpdatedAt))) throw failure('INVALID_REQUEST');
          const id = payload.id || global.crypto.randomUUID();
          const operation = saving.catch(() => {}).then(async () => {
            unchanged(); await owned(payload); unchanged();
            return adapter.save({id,name:payload.name,snapshot},{customerKey:payload.customerKey,expectedUpdatedAt:payload.expectedUpdatedAt ?? null});
          });
          saving = operation; return operation;
        }
      }
    };
    Promise.resolve().then(run).then(result => {
      if (['list','get','save'].includes(message.action)) unchanged();
      reply({ok:true,result});
    }).catch(error => {
      if (error?.code === 'AUTH_CHANGED') { sessionGeneration++; knownOwner = null; }
      reply({ok:false,error:publicError(error)});
    }).finally(() => pending.delete(message.id));
  });
})(window);
